<template>
  <ScreenSkeleton>
    <!-- «Куда ушли» is the charts' own: its caption, the ring of 212 and the legend's rows. -->
    <AppCard class="ghost-donut">
      <span class="ghost-heading"><span class="ghost-bar ghost-caption"></span></span>
      <span class="ghost-ring"></span>
      <span class="ghost-legend">
        <span v-for="n in 3" :key="n" class="ghost-row">
          <span class="ghost-dot"></span>
          <span class="ghost-bar ghost-name"></span>
          <span class="ghost-bar ghost-amount"></span>
        </span>
      </span>
    </AppCard>
    <!-- The card after it: the bars of «Расходы по месяцам» of a year — the reading above them in the
         kit's lines, the twelve bars the chart's own — or «Против обычного» of a month, the kit's card of
         lines (MOL-178: the kit's parts, and between them what is the screen's own). -->
    <AppCard v-if="year" class="ghost-next ghost-card">
      <span class="ghost-heading"><span class="ghost-bar ghost-caption"></span></span>
      <SkeletonPart kind="lines" :card="false" :widths="[46]" />
      <span class="ghost-bars">
        <span v-for="n in 12" :key="n" class="ghost-column"></span>
      </span>
    </AppCard>
    <div v-else class="ghost-next">
      <SkeletonPart kind="lines" :widths="[100, 72, 86, 54]" />
    </div>
  </ScreenSkeleton>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import AppCard from '@/components/AppCard.vue'
import ScreenSkeleton from '@/components/ScreenSkeleton.vue'
import SkeletonPart from '@/components/SkeletonPart.vue'

/**
 * Loading of «Графики» in the shape of its answer (Ф-13, MOL-178, handoff MOL-157 v2 5a): the card of
 * the ring, as large as the ring that comes, and the card that stands after it — the lines of «Против
 * обычного» on «Месяц», twelve bars on «Год». The kit's `SkeletonPart` wherever the kit has the shape;
 * the ring, the legend's rows beside it and the bars are the chart's own, in bars of `skeleton-bar`.
 */
export default defineComponent({
  name: 'ChartsSkeleton',
  components: { AppCard, ScreenSkeleton, SkeletonPart },
  props: {
    /** «Год»: the card after the ring is twelve bars, not lines. */
    year: { type: Boolean, default: false },
  },
})
</script>

<style scoped lang="scss">
.ghost-donut,
.ghost-card {
  display: grid;
  gap: var(--space-3);
  padding: var(--space-4);
}

/* 24 between the cards, as between the cards of the answer (Ф-10): the frame puts 8. */
.ghost-next {
  margin-top: var(--space-4);
}

.ghost-heading {
  display: flex;
  align-items: center;
  height: calc(1em * var(--leading-body));
  font-size: var(--text-callout);
}

.ghost-bar {
  @include skeleton-bar;

  height: var(--skeleton-caption);
}

.ghost-caption {
  width: 30%;
}

/* The ring measures its card for its band (MOL-321). */
.ghost-donut {
  container-type: inline-size;
}

/* The ring of `DonutChart`: 212, its band 12 of the hundred — and never wider than the card, as the ring
   that comes (MOL-321): a square of 13.25rem took «Графики» 47 px past a phone of 320 at 150 % while the
   answer was on its way, 169 at 200 % (adversarial Е1). */
.ghost-ring {
  @include skeleton-bar;

  width: min(13.25rem, 100%);
  aspect-ratio: 1;
  margin: 0 auto;
  border-radius: 50%;
  background: none;
  box-shadow: inset 0 0 0 calc(min(13.25rem, 100cqi) * 0.12) var(--border-strong);
}

.ghost-legend {
  display: grid;
}

.ghost-row {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-height: var(--touch-target);
  padding: 0 var(--space-2);
}

.ghost-dot {
  @include skeleton-bar;

  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
}

.ghost-name {
  flex: 1;
  max-width: 45%;
  height: var(--skeleton-line);
}

.ghost-amount {
  width: 22%;
  height: var(--skeleton-line);
  margin-left: auto;
}

.ghost-bars {
  display: flex;
  align-items: flex-end;
  gap: var(--space-1);
  height: 9.375rem;
}

.ghost-column {
  @include skeleton-bar;

  flex: 1;
  height: 55%;
  border-radius: var(--radius-sm) var(--radius-sm) var(--radius-mark) var(--radius-mark);

  &:nth-child(3n) {
    height: 70%;
  }

  &:nth-child(4n) {
    height: 40%;
  }
}
</style>
